import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, ArrowLeft, FileText, Download } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, TextInput, Alert, PageHeader, Section } from '../components/UI';

function getField(obj, ...keys) {
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== '') return obj[key];
  }
  return '';
}

function isAbnormal(resultVal, refRange) {
  if (!resultVal && resultVal !== 0) return false;
  const r = String(resultVal).trim();
  const ref = String(refRange || '').trim();
  if (/\bnormal\b/i.test(r)) return false;
  if (/^(negative|none|absent|not\s+detected)$/i.test(r)) return false;
  const numMatch = r.match(/(\d+(?:\.\d+)?)/);
  const val = numMatch ? parseFloat(numMatch[1]) : NaN;
  if (!isNaN(val) && ref) {
    const rangeMatch = ref.match(/(\d+(?:\.\d+)?)\s*[-]\s*(\d+(?:\.\d+)?)/);
    if (rangeMatch) return val < parseFloat(rangeMatch[1]) || val > parseFloat(rangeMatch[2]);
    const ltMatch = ref.match(/^<=?\s*(\d+(?:\.\d+)?)/);
    if (ltMatch) return val > parseFloat(ltMatch[1]);
    const gtMatch = ref.match(/^>=?\s*(\d+(?:\.\d+)?)/);
    if (gtMatch) return val < parseFloat(gtMatch[1]);
  }
  if (/\b(negative|none|absent|not\s+detected)\b/i.test(ref)) return true;
  return false;
}

async function fetchLogoAsBase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}

function buildHtml(result, patientName, patientId, message, logoDataUrl) {
  const report = result?.Report || result?.Result?.Report || result?.report || [];
  const disease = result?.Disease || result?.Result?.Disease || result?.disease || [];
  const date = result?.timestamp ? new Date(result.timestamp).toLocaleDateString() : new Date().toLocaleDateString();
  const time = result?.timestamp ? new Date(result.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const reportRows = report.length
    ? report.map(r => {
        const analysisType = getField(r, 'Analysis Type', 'AnalysisType', 'analysis_type', 'Parameter', 'parameter', 'Test', 'test', 'Name', 'name');
        const resultVal    = getField(r, 'Result', 'result', 'Value', 'value');
        const refRange     = getField(r, 'Ref. Range', 'Ref Range', 'RefRange', 'ref_range', 'Reference Range', 'ReferenceRange', 'Normal Range', 'NormalRange');
        const abnormal = isAbnormal(resultVal, refRange);
        const resultStyle = abnormal ? 'color:#dc2626;font-weight:700' : 'color:#2d6a4f';
        const rowStyle    = abnormal ? ' style="background:#fff5f5"' : '';
        return '<tr' + rowStyle + '>'
          + '<td>' + (analysisType || JSON.stringify(r)) + '</td>'
          + '<td style="' + resultStyle + '">' + (resultVal || '') + (abnormal ? ' &#9888;' : '') + '</td>'
          + '<td>' + (refRange || '&mdash;') + '</td>'
          + '</tr>';
      }).join('')
    : '<tr><td colspan="3" style="color:#94a3b8;font-style:italic">No analysis data available.</td></tr>';

  const diseaseRows = disease.length
    ? disease.map(d => {
        const condition = getField(d, 'Possible Disease', 'PossibleDisease', 'Disease', 'disease', 'Condition', 'condition');
        const reason    = getField(d, 'Reason', 'reason', 'Description', 'description');
        return '<tr><td>' + condition + '</td><td>' + reason + '</td></tr>';
      }).join('')
    : '<tr><td colspan="2" style="color:#94a3b8;font-style:italic">No findings.</td></tr>';

  const msgBlock = message ? '<div class="msg">' + message + '</div>' : '';

  return '<!DOCTYPE html><html><head><meta charset="utf-8"><style>'
    + 'body{font-family:Arial,sans-serif;color:#2d6a4f;margin:0;padding:0;background:#f8fafc}'
    + '.wrapper{max-width:720px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08)}'
    + '.header{background:#2d6a4f;padding:24px 28px;display:flex;align-items:center;gap:14px}'
    + '.logo{height:44px;max-width:140px;object-fit:contain}'
    + '.header h1{color:#e8f1ff;margin:0;font-size:20px;font-family:Georgia,serif}'
    + '.header p{color:rgba(255,255,255,0.7);margin:4px 0 0;font-size:12px}'
    + '.body{padding:24px 28px}'
    + '.msg{background:#f0faf4;border-left:4px solid #52b788;padding:12px 16px;border-radius:8px;margin-bottom:20px;font-size:14px;line-height:1.7}'
    + 'h3{color:#2d6a4f;font-size:15px;margin:20px 0 10px;font-family:Georgia,serif;border-bottom:2px solid #b7e4c7;padding-bottom:6px}'
    + 'table{width:100%;border-collapse:collapse;border:1px solid #b7e4c7;border-radius:8px;overflow:hidden}'
    + 'th{background:#2d6a4f;color:#fff;padding:9px 12px;text-align:left;font-size:12px}'
    + 'td{padding:9px 12px;border-bottom:1px solid #d8f3dc;font-size:13px}'
    + 'tr:last-child td{border-bottom:none}'
    + '.footer{background:#f0faf4;padding:14px 28px;font-size:11px;color:#94a3b8;text-align:center}'
    + '</style></head><body><div class="wrapper">'
    + '<div class="header">' + (logoDataUrl ? '<img class="logo" src="' + logoDataUrl + '" alt="Logo">' : '') + '<div><h1>Cytometrix Urinalysis</h1><p>Diagnostic Report</p></div></div>'
    + '<div class="body">'
    + msgBlock
    + '<h3>Patient &amp; Examination Info</h3>'
    + '<table><tr><th>Patient Name</th><th>Patient ID</th><th>Date</th><th>Time</th></tr>'
    + '<tr><td>' + (patientName || 'N/A') + '</td><td>' + (patientId || 'N/A') + '</td><td>' + date + '</td><td>' + time + '</td></tr></table>'
    + '<h3>Analysis Summary</h3>'
    + '<table><tr><th>Parameter</th><th>Result</th><th>Reference Range</th></tr>' + reportRows + '</table>'
    + '<h3>Diagnostic Insights</h3>'
    + '<table><tr><th>Possible Condition</th><th>Reason</th></tr>' + diseaseRows + '</table>'
    + '</div>'
    + '<div class="footer">This report is for wellness purposes only.</div>'
    + '</div></body></html>';
}

function downloadHtml(html, patientName, patientId) {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Urinalysis_' + (patientId || 'report') + '.html';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildPlainTextReport(result, patientName, patientId, message) {
  const report = result?.Report || result?.Result?.Report || result?.report || [];
  const disease = result?.Disease || result?.Result?.Disease || result?.disease || [];
  const date = result?.timestamp ? new Date(result.timestamp).toLocaleDateString() : new Date().toLocaleDateString();
  const time = result?.timestamp ? new Date(result.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  const lines = [];
  if (message) lines.push(message, '');
  lines.push('CYTOMETRIX URINALYSIS REPORT', '============================', '',
    'PATIENT INFORMATION',
    'Patient Name : ' + (patientName || 'N/A'),
    'Patient ID   : ' + (patientId || 'N/A'),
    'Date         : ' + date,
    'Time         : ' + time,
    '', 'ANALYSIS SUMMARY', '-----------------',
  );

  if (report.length) {
    report.forEach(r => {
      const analysisType = getField(r, 'Analysis Type', 'AnalysisType', 'Parameter', 'parameter', 'Test', 'Name');
      const resultVal    = getField(r, 'Result', 'result', 'Value', 'value');
      const refRange     = getField(r, 'Ref. Range', 'Ref Range', 'RefRange', 'ref_range', 'Reference Range', 'Normal Range');
      const flag = isAbnormal(resultVal, refRange) ? ' [ABNORMAL]' : '';
      lines.push(analysisType + ': ' + resultVal + (refRange ? '  (Ref: ' + refRange + ')' : '') + flag);
    });
  } else { lines.push('No analysis data available.'); }

  lines.push('', 'DIAGNOSTIC INSIGHTS', '-------------------');
  if (disease.length) {
    disease.forEach(d => {
      const condition = getField(d, 'Possible Disease', 'Disease', 'Condition');
      const reason    = getField(d, 'Reason', 'reason', 'Description');
      lines.push('- ' + condition + ': ' + reason);
    });
  } else { lines.push('No findings.'); }

  lines.push('', '--', 'Cytometrix Urinalysis Platform', 'For medical professional use only.');
  return lines.join('\n');
}

export default function SharePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { result, email, patientName, patientId } = location.state || {};

  const [recipient, setRecipient] = useState(email || '');
  const [subject, setSubject] = useState('Urinalysis Report - ' + (patientName || 'Patient') + ' (' + (patientId || 'N/A') + ')');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const validate = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const openMailApp = () => {
    if (!validate(recipient)) { setError('Please enter a valid email address.'); return; }
    setError('');
    const body = buildPlainTextReport(result, patientName, patientId, message);
    window.location.href = 'mailto:' + encodeURIComponent(recipient)
      + '?subject=' + encodeURIComponent(subject)
      + '&body=' + encodeURIComponent(body);
    setStatus('Your mail app should open with the report pre-filled. Review and tap Send.');
  };

  const downloadReport = async () => {
    const logoDataUrl = await fetchLogoAsBase64('/logo.png');
    const html = buildHtml(result, patientName, patientId, message, logoDataUrl);
    downloadHtml(html, patientName, patientId);
    setStatus('Report downloaded.');
  };

  return (
    <Layout>
      <style>{`
        @media (max-width: 600px) {
          .share-action-btns { flex-direction: column !important; }
          .share-action-btns > * { width: 100%; }
        }
      `}</style>

      <PageHeader
        title="Share Report"
        subtitle={'For: ' + (patientName || 'Unknown') + ' (' + (patientId || 'N/A') + ')'}
        actions={<Btn variant="secondary" icon={ArrowLeft} onClick={() => navigate(-1)}>Back</Btn>}
      />

      <div style={{ maxWidth: 620, margin: '0 auto' }}>
        {error  && <div style={{ marginBottom: 16 }}><Alert type="error">{error}</Alert></div>}
        {status && <div style={{ marginBottom: 16 }}><Alert type="success">{status}</Alert></div>}

        <Section title="Email Report">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextInput label="Recipient Email" icon={Mail} type="email" value={recipient}
              onChange={e => setRecipient(e.target.value)} placeholder="doctor@hospital.ca" />
            <TextInput label="Subject" icon={FileText} value={subject}
              onChange={e => setSubject(e.target.value)} />
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', marginBottom: 6 }}>
                Optional Message
              </label>
              <textarea
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder="Add a personal note to accompany the report..."
                style={{
                  width: '100%', minHeight: 100, padding: '10px 12px',
                  border: '1.5px solid var(--gray-200)', borderRadius: 10,
                  fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                  resize: 'vertical', outline: 'none', lineHeight: 1.6, boxSizing: 'border-box',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                onBlur={e  => e.target.style.borderColor = 'var(--gray-200)'}
              />
            </div>
            <div className="share-action-btns" style={{ display: 'flex', gap: 10 }}>
              <Btn icon={Mail} onClick={openMailApp} style={{ flex: 1 }}>Open in Mail</Btn>
              <Btn variant="secondary" icon={Download} onClick={downloadReport}>Download HTML</Btn>
            </div>
          </div>
        </Section>

        <div style={{ background: '#f0faf4', borderRadius: 12, padding: '12px 16px', border: '1px solid rgba(82,183,136,0.3)', marginBottom: 14 }}>
          <p style={{ fontSize: 13, color: '#2d6a4f', fontWeight: 600, marginBottom: 4 }}>How it works</p>
          <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.7 }}>
            <strong>Open in Mail</strong> — opens your mail app with the report pre-filled. Abnormal values are flagged [ABNORMAL].<br />
            <strong>Download</strong> — saves an HTML report with abnormal values highlighted in red.
          </p>
        </div>

        <div style={{ background: 'var(--amber-soft)', borderRadius: 12, padding: '12px 16px', border: '1px solid rgba(217,119,6,0.2)' }}>
          <p style={{ fontSize: 13, color: 'var(--amber)', fontWeight: 600, marginBottom: 4 }}>Medical Information Notice</p>
          <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6 }}>
            This report contains confidential medical information. Only share with authorized healthcare providers.
          </p>
        </div>
      </div>
    </Layout>
  );
}
